import { ref } from 'vue'

const updateAvailable = ref(false)

// "A new service worker installed" does not always mean THIS page is outdated:
// the install finishes in the background and can complete right after a reload
// that already delivered the new build (it can also be re-reported on load while
// the new worker is briefly in the `waiting` state). Showing the banner then
// would ask the user to refresh into the version they are already running.
// So before showing it, fetch the server's current index.html and check whether
// it still references the exact app bundle this page is executing.
async function isPageOutdated() {
    // Vite emits a single entry module script, e.g.
    // <script type="module" crossorigin src="/assets/index-<hash>.js">
    const appScript = document.querySelector('script[type="module"][src*="assets/"]')

    if (!appScript) {
        return true
    }

    try {
        const response = await fetch('/', { cache: 'no-store' })

        // A non-2xx (e.g. 502/503 mid-deploy) would read an error page that never
        // contains the bundle name — don't invite the user to reload into it.
        if (!response.ok) {
            return false
        }

        const html = await response.text()
        const bundleFilename = appScript.src.split('/').pop()

        return !html.includes(bundleFilename)
    } catch {
        // Can't verify (e.g. offline) — better a redundant banner than a missed update.
        return true
    }
}

// Called from registerServiceWorker.js when a new service worker has installed,
// i.e. a new version of the app has been deployed.
export async function notifyUpdateAvailable() {
    updateAvailable.value = await isPageOutdated()
}

export default function useAppUpdate() {
    const reload = () => {
        window.location.reload()
        // Reloading right after a service-worker changeover can leave the
        // navigation hanging forever (the freshly activated worker's network
        // fetch gets stuck pending — Chromium race). If this page is still
        // alive in 5s the reload never completed: reloading again cancels the
        // stuck navigation and retries on a clean slate, mimicking the manual
        // second press that always works. If the first reload succeeds, the
        // page unloads and this timer dies with it.
        setTimeout(() => window.location.reload(), 5000)
    }

    return { updateAvailable, reload }
}
